import {type DocumentNode, gql} from '@apollo/client';
import {
    type Filter,
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import type {PayoutReport, QueryExportPayoutReportsArgs, QueryGetPayoutReportsArgs} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';

import type {PayoutReportDownloadFields, PayoutReportFilterKeys, PayoutReportQueryFields, PayoutReportTextFilterKeys} from './types';

export class PayoutReportRequestBuilder
    extends GqlRequestBuilder<EntityType, QueryGetPayoutReportsArgs, PayoutReportQueryFields, PayoutReportFilterKeys>
    implements IDownloadByLinkRequestBuilder<QueryExportPayoutReportsArgs, PayoutReportDownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<PayoutReportDownloadFields>
    ): GqlRequest<QueryExportPayoutReportsArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected buildQuery(fields: PayoutReportQueryFields[]): DocumentNode {
        const requiredFields: PayoutReportQueryFields[] = ['payout_report_id'];
        const queryFields: PayoutReportQueryFields[] = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetPayoutReports($filter: PayoutReportsFilter, $sort: Sorting, $start: Int, $end: Int) {
                getPayoutReports(filter: $filter, sort: $sort, end: $end, start: $start) {
                    items {
                        ${this.buildQueryFields(queryFields)}
                    }
                    total_count
                }
            }
        `;
    }

    protected buildFilter(filter: Filter<PayoutReportFilterKeys>): QueryGetPayoutReportsArgs {
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: PayoutReportTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                transaction_date: this.toGQLDateRange(
                    this.getLatestTimestamp(filter.transaction_date_from as number, filter.transaction_date_min as number),
                    filter.transaction_date_to
                ),
            },
        };
    }

    protected validateFilters(_filter: Partial<Record<PayoutReportFilterKeys, unknown>>): void {
        return;
    }

    private readonly filterFieldsMapper: Record<PayoutReportTextFilterKeys, (keyof PayoutReport)[]> = {
        player_and_agent_uid_username: ['player_uid', 'player_username', 'agent_uid', 'agent_username'],
        player_uid: ['player_uid'],
        player_username: ['player_username'],
        agent_uid: ['agent_uid'],
        agent_username: ['agent_username'],
        country: ['country'],
    };

    private buildExportQuery() {
        return gql`
            query ExportPayoutReports(
                $filter: PayoutReportsFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportPayoutReports(filter: $filter, report_name: $report_name, fields: $fields, sort: $sort, end: $end, start: $start) {
                    url
                }
            }
        `;
    }
}
